import { useMemo } from 'react';

import { Stack, Typography, Link } from '@mui/material';

import parse, { HTMLReactParserOptions, Element } from 'html-react-parser';

import Card from '@/components/card';
import { Posts } from '@/models/posts';
import { replaceNewLinesWithBreaks } from '@/utils/text';

interface CreatorPostKeywordsDetailsProps {
  postData: Posts.Details;
}

const options: HTMLReactParserOptions = {
  replace: (domNode) => {
    if (domNode instanceof Element) {
      const { attribs } = domNode;
      if (attribs?.href)
        return (
          <Link href={attribs.href} target="_blank">
            {attribs.href}
          </Link>
        );
    }
    return domNode;
  }
};

const CreatorPostKeywordsDetails = ({
  postData
}: CreatorPostKeywordsDetailsProps) => {
  const tags = useMemo(
    () =>
      postData?.tags
        ?.map((item: { username: string }) =>
          !item.username.startsWith('@') ? `@${item.username}` : item.username
        )
        .join(' ') || '',
    [postData?.tags]
  );

  const hashtags = useMemo(
    () =>
      postData?.hashtags
        ?.map((item: any) => {
          if (typeof item === 'object' && item !== null && item.hashtagName) {
            const { hashtagName } = item;
            if (typeof hashtagName !== 'string') return null;
            return hashtagName.startsWith('#')
              ? hashtagName
              : `#${hashtagName}`;
          }
          if (typeof item === 'string') {
            return item.startsWith('#') ? item : `#${item}`;
          }
          return null;
        })
        .filter(Boolean)
        .join(' ') || '',
    [postData?.hashtags]
  );

  const links = useMemo(
    () => postData?.links?.filter(({ isValid }) => isValid) ?? [],
    [postData?.links]
  );

  const description = useMemo(
    () => replaceNewLinesWithBreaks(postData?.description || ''),
    [postData?.description]
  );

  const hasContent =
    postData?.description ||
    postData?.tags?.length ||
    postData?.hashtags?.length ||
    postData?.links?.length;

  if (!hasContent) return <></>;

  return (
    <Card
      sx={{ marginBottom: 2 }}
      isCollapsible
      title="Details"
      content={
        <Stack gap={2}>
          {postData?.description && (
            <Stack>
              <Typography variant="body1" fontWeight="500">
                Description
              </Typography>
              <Typography
                variant="caption"
                sx={{ overflowY: 'auto', maxHeight: 240, paddingBottom: 1 }}
              >
                {parse(description, options)}
              </Typography>
            </Stack>
          )}
          {tags && (
            <Stack>
              <Typography variant="body1" fontWeight="500">
                Tags
              </Typography>
              <Typography variant="caption">{parse(tags)}</Typography>
            </Stack>
          )}
          {hashtags && (
            <Stack>
              <Typography variant="body1" fontWeight="500">
                Hashtags
              </Typography>
              <Typography variant="caption">{parse(hashtags)}</Typography>
            </Stack>
          )}
          {links.length > 0 && (
            <Stack>
              <Typography variant="body1" fontWeight="500">
                Links
              </Typography>
              <Stack
                sx={{ overflowY: 'auto', maxHeight: 240, paddingBottom: 1 }}
                gap={0.5}
              >
                {links.map(({ url }) => (
                  <Link
                    key={url}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="caption"
                    underline="hover"
                    color="text.primary"
                  >
                    {url}
                  </Link>
                ))}
              </Stack>
            </Stack>
          )}
        </Stack>
      }
    />
  );
};

export default CreatorPostKeywordsDetails;
